/* eslint-disable @typescript-eslint/ban-ts-comment */
import dayjs from "dayjs";
import { SortCourseBy, SortJobsBy } from "interfaces";
import moment from "jalali-moment";
import jalaliday from "jalaliday";

dayjs.extend(jalaliday);

export const isEmpty = (obj: any) => {
  if (obj === null || obj === "" || obj === undefined) return true;
  if (!Object.entries(obj).length) return true;
  return false;
};

export const asyncLocalStorage = {
  setItem: function (key: string, value: any) {
    return Promise.resolve().then(function () {
      localStorage.setItem(key, value);
    });
  },
  getItem: function (key: any) {
    return Promise.resolve().then(function () {
      return localStorage.getItem(key);
    });
  },
};

export function generateUuidForReactList(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
    .replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    })
    .toUpperCase();
}

export function blobToFile(theBlob: any, fileName: any) {
  //A Blob() is almost a File() - it's just missing the two properties below which we will add
  theBlob.lastModifiedDate = new Date();
  theBlob.name = fileName;
  return theBlob;
}

export const convertToCustomMiladiFormat = (
  date: string,
  format = "YYYY-MM-DD"
): string => {
  return moment?.from(date, "fa", "YYYY/MM/DD")?.format(format);
};

export const commafy = (num: any = 0, delimiterChar = ","): any => {
  try {
    let number = num?.toString()?.replace(".*", "");
    number = number?.split(delimiterChar)?.join("");
    if (isNaN(Number(number))) {
      return number;
    }
    const str = number?.toString()?.split(".");
    if (str[0]?.length >= 4) {
      str[0] = str[0]?.replace(/(\d)(?=(\d{3})+$)/g, "$1" + delimiterChar);
    }
    if (str[1] && str[1]?.length >= 4) {
      str[1] = str[1]?.replace(/(\d{3})/g, "$1 ");
    }

    return str.join(".");
  } catch (error) {
    console.error(error);
  }
};
export const getCurrentDate = (format = "jYYYY/jMM/jDD") => {
  return moment().format(format);
};
export const convertTimeStamp = (timeStamp: any) => {
  try {
    const d = moment(timeStamp);
    return `${d.hour()}:${d.minute()}:${d.second()}`;
  } catch (error) {
    console.log(error);
  }
};
export const onlyNumber = (e: any) => {
  return (e.target.value = toEnglishDigits(e?.target?.value)
    ?.replace(/[^0-9.]/g, "")
    ?.replace(/(\..*?)\..*/g, "$1"));
};

export const getActiveClassFilterBtn = (arg: boolean) =>
  arg ? "bg-blue text-white p-1 rounded" : "bg-white text-blue";

export const convertToJalali = (value: string) => {
  if (value) {
    const date = value.split("T") ? value.split("T")[0] : value;
    return moment.from(date, "en", "YYYY-MM-DD").locale("fa").format("DD MMMM");
  }
  return undefined;
};

const TEHRAN_UTC_OFFSET = "+03:30";

export const formatJalaliDateFromIso = (value: string) => {
  if (value) {
    return moment(value)
      .utcOffset(TEHRAN_UTC_OFFSET)
      .locale("fa")
      .format("jYYYY/jMM/jDD");
  }
  return undefined;
};

export const formatTimeFromIso = (value: string) => {
  if (value) {
    return moment(value)
      .utcOffset(TEHRAN_UTC_OFFSET)
      .locale("fa")
      .format("HH:mm");
  }
  return undefined;
};

export const decodeRichHtmlEntities = (html: string): string => {
  if (!html) return "";

  let out = html;
  for (let i = 0; i < 3; i += 1) {
    out = out
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;/gi, "'")
      .replace(/&amp;/gi, "&");
  }
  return out;
};

export const getNameAndEnumForCourseType = (type: any) => {
  if (typeof type == "string") {
    switch (type) {
      case "in-class":
        return { id: 0, name: "in-class" };
      case "online":
        return { id: 1, name: "online" };
      case "ofline":
        return { id: 2, name: "ofline" };
      case "free":
        return { id: 3, name: "free" };
    }
  } else {
    switch (type) {
      case 0:
        return { id: 0, name: "in-class" };
      case 1:
        return { id: 1, name: "online" };
      case 2:
        return { id: 2, name: "ofline" };
      case 3:
        return { id: 3, name: "free" };
    }
  }
};

export function toEnglishDigits(str: any) {
  // convert persian digits [۰۱۲۳۴۵۶۷۸۹]
  let e = "۰".charCodeAt(0);
  str = str.replace(/[۰-۹]/g, function (t: any) {
    return t.charCodeAt(0) - e;
  });

  // convert arabic indic digits [٠١٢٣٤٥٦٧٨٩]
  e = "٠".charCodeAt(0);
  str = str.replace(/[٠-٩]/g, function (t: any) {
    return t.charCodeAt(0) - e;
  });
  return str;
}

export const extractNumericIdFromCatchAll = (
  rawId: unknown
): string | undefined => {
  if (rawId === null || rawId === undefined) return undefined;

  const parts = Array.isArray(rawId) ? rawId : [rawId];
  const joined = parts.map((p) => String(p ?? "")).join("/");
  const normalized = toEnglishDigits(joined);

  const m = normalized.match(/^(\d+)/);
  if (m?.[1]) return m[1];

  for (const part of parts) {
    const s = toEnglishDigits(String(part ?? ""));
    if (/^\d+$/.test(s)) return s;
  }

  return undefined;
};

export const convertMiladiToJalali = (time: any) => {
  if (time) {
    try {
      const date =
        time.split("T") && time.split("T").length ? time.split("T")[0] : time;
      const khorshidi = moment(date).locale("fa").format("jYYYY/jMM/jDD");
      return khorshidi;
    } catch (error) {
      console.log(error);
    }
  }
};

export const toEnDigit = (n: string | any): string | number =>
  n.replace(/[٠-٩۰-۹]/g, (n: string) => 15 & n.charCodeAt(0));

function pad(d: number | string) {
  const num = typeof d === "number" ? d : Number(d);
  return num < 10 ? "0" + num.toString() : num.toString();
}

export const timeConvert = (n: any) => {
  const hours = n / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  return pad(rhours) + ":" + pad(rminutes);
};

export function removeEmptyObj(obj: any) {
  Object.keys(obj).forEach((key) => obj[key] === "" && delete obj[key]);
  return obj;
}

export const getFormNowDate = (time: Date) =>
  moment(time).locale("fa").fromNow();

export const ConvertToMiladi = (time: any) => {
  const m = moment(time, "jYYYY/jM/jD");
  const date = m.format();
  return date;
};

export const getClassFormat = (type: number) => {
  switch (type) {
    case 0:
      return "حضوری";
    case 1:
      return "آنلاین";
    case 2:
      return "آفلاین";
  }
};

export const getColorByEnum = (enumValue: number) => {
  switch (enumValue) {
    case 0:
      return { fontColor: "#50A5F1", bgColor: "rgba(80, 165, 241, 0.2)" };
    case 1:
      return { fontColor: "#34C38F", bgColor: "rgba(52, 195, 143, 0.2)" };
    case 2:
      return { fontColor: "#F1B44C", bgColor: "rgba(241, 180, 76, 0.2)" };
    default:
      return { fontColor: "#50A5F1", bgColor: "rgba(241, 180, 76, 0.2)" };
  }
};

export const getColorByEnumV2 = (enumValue: number) => {
  switch (enumValue) {
    case 0:
      return { fontColor: "#50A5F1", bgColor: "rgba(80, 165, 241, 0.2)" };
    case 1:
      return { fontColor: "#4F9B0B", bgColor: "rgba(79, 155, 11, 0.2)" };
    case 2:
      return { fontColor: "#F1B44C", bgColor: "rgba(241, 180, 76, 0.2)" };
    default:
      return { fontColor: "#50A5F1", bgColor: "rgba(241, 180, 76, 0.2)" };
  }
};
export const getColorByEnumV3 = (enumValue: number) => {
  switch (enumValue) {
    case 0:
      return { fontColor: "#fff", bgColor: "#50A5F1" };
    case 1:
      return { fontColor: "#fff", bgColor: "#4F9B0B" };
    case 2:
      return { fontColor: "#fff", bgColor: "#EE8A1E" };
    default:
      return { fontColor: "#fff", bgColor: "#EE8A1E" };
  }
};

export function sortArrayByOrder(array: any[], order: string) {
  return array?.sort((a: any, b: any) => {
    if (a[order] < b[order]) {
      return -1;
    }
    if (a[order] > b[order]) {
      return 1;
    }
    return 0;
  });
}

export const convertNumToStringJalaliMonth = (arr: []) => {
  const months = [
    "فروردين",
    "ارديبهشت",
    "خرداد",
    "تير",
    "مرداد",
    "شهريور",
    "مهر",
    "آبان",
    "آذر",
    "دي",
    "بهمن",
    "اسفند",
  ];
  const newMonth = arr?.map((a: number) => months[a - 1]);
  return newMonth;
};

export function getTitleByEnum(enumValue: number) {
  switch (enumValue) {
    case SortCourseBy.MostlySelled:
      return "پرفروش ترین ها";
    case SortCourseBy.MostlyViewed:
      return "بیشترین بازدید";
    case SortCourseBy.RecentlyUpdated:
      return "جدیدترین دوره";
    case SortCourseBy.SelectedByUsers:
      return "منتخب کاربران";
    case SortCourseBy.RecentlyAdded:
      return "مرتبط ترین";
  }
}

export function getJobByEnum(enumValue: number) {
  switch (enumValue) {
    case SortJobsBy.Archive:
      return "فرصت‌های شغلی آرشیو";
    case SortJobsBy.MostlyViewed:
      return "بیشترین بازدید";
    case SortJobsBy.RecentlyAdded:
      return "جدیدترین فرصت‌های شغلی";
  }
}

export const loadState = () => {
  try {
    const serializedState = localStorage.getItem("state");
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
};
export const saveState = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("state", serializedState);
  } catch (err) {
    // Ignore write errors.
  }
};
export const throttle = (fn: () => void, delay: number): VoidFunction => {
  let last = 0;
  return (...arg) => {
    const now = new Date().getTime();
    if (now - last < delay) {
      return;
    }
    last = now;
    return fn(...arg);
  };
};
export const convertNumberToPriceFormat = (value: number): string => {
  if (value === null) return "";
  if (typeof value !== "number") return "";
  const val = Number(value);
  return val.toLocaleString().replaceAll(",", ".");
};
